import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ToastProvider } from "@/state/ToastContext";
import { AuthProvider } from "@/state/AuthContext";
import { WorkspaceDraftProvider } from "@/state/WorkspaceDraftContext";
import { AiDepthProvider } from "@/state/AiDepthContext";
import { CommandPaletteProvider } from "@/state/CommandPaletteContext";
import { CommandPalette } from "@/components/layout/CommandPalette";
import { AppShell } from "@/components/layout/AppShell";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";
import { Spinner } from "@/components/ui/Meter";
import { LandingPage } from "@/pages/LandingPage";

const AuthPage = lazy(() => import("@/pages/AuthPage").then((module) => ({ default: module.AuthPage })));
const DemoPage = lazy(() => import("@/pages/DemoPage").then((module) => ({ default: module.DemoPage })));
const GuidePage = lazy(() => import("@/pages/GuidePage").then((module) => ({ default: module.GuidePage })));
const WorkbenchPage = lazy(() => import("@/pages/WorkbenchPage").then((module) => ({ default: module.WorkbenchPage })));
const ProjectPage = lazy(() => import("@/pages/ProjectPage").then((module) => ({ default: module.ProjectPage })));
const LiteratureSearchPage = lazy(() => import("@/pages/LiteratureSearchPage").then((module) => ({ default: module.LiteratureSearchPage })));
const CheckupEntryPage = lazy(() => import("@/pages/CheckupEntryPage").then((module) => ({ default: module.CheckupEntryPage })));
const CheckupReportPage = lazy(() => import("@/pages/CheckupReportPage").then((module) => ({ default: module.CheckupReportPage })));
const ScanToolPage = lazy(() => import("@/pages/tools/ScanToolPage").then((module) => ({ default: module.ScanToolPage })));
const DataCheckToolPage = lazy(() => import("@/pages/tools/DataCheckToolPage").then((module) => ({ default: module.DataCheckToolPage })));
const HistoryPage = lazy(() => import("@/pages/HistoryPage").then((module) => ({ default: module.HistoryPage })));
const ArchivedResultPage = lazy(() => import("@/pages/ArchivedResultPage").then((module) => ({ default: module.ArchivedResultPage })));
const LegacyRoute = lazy(() => import("@/pages/ArchivedResultPage").then((module) => ({ default: module.LegacyRoute })));
const AccountPage = lazy(() => import("@/pages/AccountPage").then((module) => ({ default: module.AccountPage })));
const SettingsPage = lazy(() => import("@/pages/SettingsPage").then((module) => ({ default: module.SettingsPage })));
const SharePage = lazy(() => import("@/pages/SharePage").then((module) => ({ default: module.SharePage })));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage").then((module) => ({ default: module.NotFoundPage })));
const PrivacyPage = lazy(() => import("@/pages/LegalPage").then((module) => ({ default: module.PrivacyPage })));
const TermsPage = lazy(() => import("@/pages/LegalPage").then((module) => ({ default: module.TermsPage })));

function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ToastProvider>
          <AuthProvider>
            <WorkspaceDraftProvider>
              <AiDepthProvider>
                <CommandPaletteProvider>
                  <Suspense fallback={<div className="core-public cluster" role="status"><Spinner />正在载入页面…</div>}>
                    <Routes>
                      <Route path="/" element={<LandingPage />} />
                      <Route path="/auth" element={<AuthPage />} />
                      <Route path="/demo" element={<DemoPage />} />
                      <Route path="/guide" element={<GuidePage />} />
                      <Route path="/share/:token" element={<SharePage />} />
                      <Route path="/terms" element={<TermsPage />} />
                      <Route path="/privacy" element={<PrivacyPage />} />
                      <Route path="/app" element={<AppShell />}>
                        <Route index element={<WorkbenchPage />} />
                        <Route path="projects/:id" element={<ProjectPage />} />
                        <Route path="literature" element={<LiteratureSearchPage />} />
                        <Route path="checkup" element={<CheckupEntryPage />} />
                        <Route path="checkup/:jobId" element={<CheckupReportPage />} />
                        <Route path="data" element={<DataCheckToolPage />} />
                        <Route path="tools/scan" element={<Navigate to="/app/checkup?mode=image" replace />} />
                        <Route path="tools/scan/:jobId" element={<ScanToolPage />} />
                        <Route path="history" element={<HistoryPage />} />
                        <Route path="history/:source/:id" element={<ArchivedResultPage />} />
                        <Route path="account" element={<AccountPage />} />
                        <Route path="settings" element={<SettingsPage />} />
                        <Route path="guide" element={<GuidePage />} />
                        <Route path="*" element={<LegacyRoute />} />
                      </Route>
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Suspense>
                  <CommandPalette />
                </CommandPaletteProvider>
              </AiDepthProvider>
            </WorkspaceDraftProvider>
          </AuthProvider>
        </ToastProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
export default App;
